import { formatPrice, formatSize } from '../../lib/lighterApi'

const DEFAULT_MAX_SIZE = 1000

function parseNum(value) {
  const n = Number(String(value ?? '').replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}

function findSummaryValue(formRoot, label) {
  const rows = [...formRoot.querySelectorAll('.flex.w-full.justify-between')]
  for (const row of rows) {
    const p = row.querySelector('p')
    if ((p?.textContent || '').trim() !== label) continue
    return row.querySelector('.tabular-nums span, .tabular-nums')
  }
  return null
}

function setText(el, text) {
  if (!el) return
  el.textContent = text
}

/**
 * @param {HTMLElement} page
 */
export function mountOrderFormControls(page) {
  const form =
    page.querySelector('#place-order') ||
    page.querySelector('[data-trade-panel="order-form"]')
  if (!form) return () => {}

  if (typeof form.__orderFormCleanup === 'function') {
    form.__orderFormCleanup()
  }

  let pct = parseNum(
    form.querySelector('[data-testid="percentage-slider-input"]')?.value
  )
  let amountUnit = 'base'
  let dragging = false

  const sizeInput = () =>
    form.querySelector('[data-testid="place-order-size-input"]')
  const pctInput = () =>
    form.querySelector('[data-testid="percentage-slider-input"]')
  const thumb = () => form.querySelector('[role="slider"]')

  function sliderRoot() {
    return thumb()?.closest('[data-orientation="horizontal"].relative.flex') || null
  }

  function trackEl() {
    const root = sliderRoot()
    if (!root) return null
    return (
      [...root.children].find(
        (el) =>
          el instanceof HTMLElement &&
          el !== thumb()?.parentElement &&
          el.classList.contains('relative')
      ) || null
    )
  }

  function rangeFill() {
    const track = trackEl()
    if (!track) return null
    // Inner pill that paints the filled range
    const candidates = [...track.querySelectorAll('span.rounded-pill')]
    return candidates.find((el) => el !== track) || candidates[0] || null
  }

  function maxSize() {
    const available = parseNum(page.dataset.availableSize)
    if (available > 0) return available
    return DEFAULT_MAX_SIZE
  }

  function markPrice() {
    return parseNum(page.dataset.markPrice) || 0
  }

  function setThumb(nextPct) {
    const el = thumb()
    const root = sliderRoot()
    if (!el || !root) return

    el.setAttribute('aria-valuenow', String(Math.round(nextPct)))

    const wrap = el.parentElement
    if (wrap) {
      wrap.style.position = 'absolute'
      wrap.style.left = `${nextPct}%`
      wrap.style.top = '50%'
      wrap.style.transform = 'translate(-50%, -50%)'
      wrap.style.zIndex = '10'
      wrap.style.display = 'flex'
      wrap.style.alignItems = 'center'
      wrap.style.justifyContent = 'center'
    }

    // Ensure root can host absolute thumb
    if (getComputedStyle(root).position === 'static') {
      root.style.position = 'relative'
    }

    const fill = rangeFill()
    if (fill) {
      fill.style.left = '2px'
      fill.style.right = 'auto'
      fill.style.width = `calc(${nextPct}% - 2px)`
      fill.style.maxWidth = 'calc(100% - 4px)'
    }
  }

  function updateSummary(sizeBase) {
    const mark = markPrice()
    const value = sizeBase * mark
    const taker = parseNum(page.dataset.takerFee)
    const maker = parseNum(page.dataset.makerFee)
    const fmtFee = (f) => {
      const p = f * 100
      if (!p) return '0'
      return String(Number(p.toFixed(4)))
    }

    const sizeEl = findSummaryValue(form, 'Order Size')
    const valueEl = findSummaryValue(form, 'Order Value')
    const estEl = findSummaryValue(form, 'Est. Price')
    const feesEl = findSummaryValue(form, 'Fees')
    const marginEl = findSummaryValue(form, 'Position Margin')

    if (sizeBase > 0) {
      setText(sizeEl, `${formatSize(sizeBase, 2)} ${page.dataset.symbol || 'LIT'}`)
      setText(
        valueEl,
        mark
          ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : '-'
      )
      setText(estEl, mark ? formatPrice(mark, 4) : '-')
      const margin = value * 0.2
      setText(
        marginEl,
        `$${margin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      )
    } else {
      setText(sizeEl, '-')
      setText(valueEl, '-')
      setText(estEl, mark ? formatPrice(mark, 4) : '-')
      setText(marginEl, '$0.00')
    }
    setText(feesEl, `Taker: ${fmtFee(taker)}% | Maker: ${fmtFee(maker)}%`)
  }

  function applyPct(nextPct, { fromInput } = {}) {
    pct = Math.min(100, Math.max(0, Number(nextPct) || 0))
    const max = maxSize()
    const sizeBase = (max * pct) / 100

    const pIn = pctInput()
    if (pIn && !fromInput) pIn.value = String(Math.round(pct))

    const sIn = sizeInput()
    if (sIn) {
      if (amountUnit === 'quote') {
        const usd = sizeBase * markPrice()
        sIn.value = usd ? usd.toFixed(2) : ''
      } else {
        sIn.value = sizeBase ? sizeBase.toFixed(2) : ''
      }
    }

    setThumb(pct)
    updateSummary(sizeBase)
  }

  function applySizeFromInput() {
    const sIn = sizeInput()
    if (!sIn) return
    const raw = parseNum(sIn.value)
    const sizeBase =
      amountUnit === 'quote' && markPrice() ? raw / markPrice() : raw
    const max = maxSize()
    pct = max > 0 ? Math.min(100, (sizeBase / max) * 100) : 0
    const pIn = pctInput()
    if (pIn) pIn.value = String(Math.round(pct))
    setThumb(pct)
    updateSummary(sizeBase)
  }

  function pctFromPointer(clientX) {
    const root = sliderRoot()
    if (!root) return pct
    const rect = root.getBoundingClientRect()
    if (rect.width <= 0) return pct
    return Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100))
  }

  const onPointerDown = (event) => {
    if (event.button != null && event.button !== 0) return
    if (event.target.closest('input, [data-testid="coin-selector"]')) return
    if (event.target.closest('[aria-label^="Set slider value"]')) return

    const root = sliderRoot()
    if (!root || !root.contains(event.target)) return

    dragging = true
    root.setPointerCapture?.(event.pointerId)
    applyPct(pctFromPointer(event.clientX))
    event.preventDefault()
  }

  const onPointerMove = (event) => {
    if (!dragging) return
    applyPct(pctFromPointer(event.clientX))
  }

  const onPointerUp = (event) => {
    if (!dragging) return
    dragging = false
    const root = sliderRoot()
    root?.releasePointerCapture?.(event.pointerId)
  }

  const onKeyDown = (event) => {
    const el = thumb()
    if (!el || document.activeElement !== el) return
    let next = pct
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next += 1
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next -= 1
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = 100
    else if (event.key === 'PageUp') next += 10
    else if (event.key === 'PageDown') next -= 10
    else return
    event.preventDefault()
    applyPct(next)
  }

  const onClick = (event) => {
    const preset = event.target.closest('[aria-label^="Set slider value to"]')
    if (preset && form.contains(preset)) {
      const match = (preset.getAttribute('aria-label') || '').match(/(\d+)/)
      if (match) applyPct(Number(match[1]))
      return
    }

    const coin = event.target.closest('[data-testid="coin-selector"]')
    if (coin && form.contains(coin)) {
      event.preventDefault()
      amountUnit = amountUnit === 'base' ? 'quote' : 'base'
      const label = coin.querySelector('.label-meta')
      const sym = page.dataset.symbol || 'LIT'
      if (label) label.textContent = amountUnit === 'base' ? sym : 'USD'
      coin.setAttribute(
        'aria-label',
        amountUnit === 'base' ? 'Switch to USD' : `Switch to ${sym}`
      )
      applyPct(pct)
      return
    }

    const box = event.target
      .closest('label')
      ?.querySelector(
        '[data-testid="tpsl-checkbox"], [data-testid="reduce-only-checkbox"]'
      )
    if (box && form.contains(box)) {
      event.preventDefault()
      const next = !box.checked
      form
        .querySelectorAll(
          '[data-testid="tpsl-checkbox"], [data-testid="reduce-only-checkbox"]'
        )
        .forEach((other) => {
          const checked = other === box && next
          other.removeAttribute('readonly')
          other.checked = checked
          other.setAttribute('data-state', checked ? 'checked' : 'unchecked')
          other
            .closest('label')
            ?.querySelector('.check-mark')
            ?.classList.toggle('is-checked', checked)
        })
    }
  }

  const onInput = (event) => {
    const t = event.target
    if (!(t instanceof HTMLInputElement)) return
    if (t.matches('[data-testid="percentage-slider-input"]')) {
      applyPct(parseNum(t.value), { fromInput: true })
    }
    if (t.matches('[data-testid="place-order-size-input"]')) {
      applySizeFromInput()
    }
  }

  const onMarketTick = () => {
    const sIn = sizeInput()
    const sizeBase =
      amountUnit === 'quote' && markPrice()
        ? parseNum(sIn?.value) / markPrice()
        : parseNum(sIn?.value)
    updateSummary(sizeBase || (maxSize() * pct) / 100)
    try {
      const settings = JSON.parse(
        localStorage.getItem('lighter:user-settings') || '{}'
      )
      const maxSlip = Number(settings.maxSlippage) || 1
      const slipBtn = form.querySelector('[data-testid="slippage-summary"] span')
      if (slipBtn) slipBtn.textContent = `Est: 0.00% | Max: ${maxSlip}%`
    } catch {
      /* ignore */
    }
  }

  const onSettings = () => onMarketTick()

  // Position 25/50/75 hit targets on the track
  form.querySelectorAll('[aria-label^="Set slider value to"]').forEach((btn) => {
    const match = (btn.getAttribute('aria-label') || '').match(/(\d+)/)
    if (!match) return
    btn.style.left = `${match[1]}%`
    btn.style.top = '50%'
    btn.style.transform = 'translate(-50%, -50%)'
  })

  const root = sliderRoot()
  root?.addEventListener('pointerdown', onPointerDown)
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('pointercancel', onPointerUp)
  form.addEventListener('click', onClick)
  form.addEventListener('input', onInput)
  form.addEventListener('keydown', onKeyDown)
  page.addEventListener('lighter:market-tick', onMarketTick)
  window.addEventListener('lighter:settings', onSettings)

  applyPct(pct)
  onMarketTick()

  form
    .querySelectorAll(
      '[data-testid="tpsl-checkbox"], [data-testid="reduce-only-checkbox"]'
    )
    .forEach((box) => {
      box.checked = false
      box.removeAttribute('checked')
      box.setAttribute('data-state', 'unchecked')
      const visual = box.parentElement?.querySelector(
        'span.flex.size-\\[14px\\], span.flex'
      )
      visual?.classList.add('check-mark')
      visual?.classList.remove('is-checked')
    })

  const cleanup = () => {
    root?.removeEventListener('pointerdown', onPointerDown)
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    window.removeEventListener('pointercancel', onPointerUp)
    form.removeEventListener('click', onClick)
    form.removeEventListener('input', onInput)
    form.removeEventListener('keydown', onKeyDown)
    page.removeEventListener('lighter:market-tick', onMarketTick)
    window.removeEventListener('lighter:settings', onSettings)
  }
  form.__orderFormCleanup = cleanup
  page.__orderFormCleanup = cleanup
  return cleanup
}
